import { Flame, Beef, Plus } from 'lucide-react';
import { DbMeal } from '@/types/database';
import { Button } from '@/components/ui/button';
import { formatPrice } from '@/lib/utils';

interface MealCardDbProps {
  meal: DbMeal;
  onCustomize: (meal: DbMeal) => void;
}

// Placeholder image for meals without images
const placeholderImage = '/placeholder.svg';

export function MealCardDb({ meal, onCustomize }: MealCardDbProps) {
  return (
    <div className="meal-card group relative overflow-hidden transition-all duration-500 hover:shadow-2xl hover:-translate-y-2">
      {/* Glow Effect */}
      <div className="absolute inset-0 bg-gradient-to-t from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
      
      <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
        <img
          src={meal.image_url || placeholderImage}
          alt={meal.name_he}
          className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:rotate-1"
          onError={(e) => {
            e.currentTarget.src = placeholderImage;
          }}
        />
        
        {/* Overlay on Hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        
        {/* Shine Effect */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out" />
        </div>
        
        <div className="absolute top-3 right-3 flex gap-2">
          {meal.calories && (
            <span className="nutrition-badge bg-card/90 backdrop-blur-sm text-foreground transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-md">
              <Flame className="h-3 w-3 text-primary" />
              {meal.calories} קל׳
            </span>
          )}
          {meal.protein && (
            <span className="nutrition-badge bg-card/90 backdrop-blur-sm text-foreground transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-md">
              <Beef className="h-3 w-3 text-primary" />
              {meal.protein}g חלבון
            </span>
          )}
        </div>
      </div>

      <div className="p-5 relative">
        <h3 className="text-lg font-bold text-foreground mb-2 transition-colors duration-300 group-hover:text-primary">
          {meal.name_he}
        </h3>
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
          {meal.description_he || meal.description}
        </p>

        <div className="flex items-center justify-between">
          <Button
            onClick={() => onCustomize(meal)}
            size="sm"
            className="gap-1 relative overflow-hidden transition-all duration-300 hover:shadow-lg hover:scale-105"
          >
            <span className="relative z-10 flex items-center gap-1">
              <Plus className="h-4 w-4 transition-transform duration-300 group-hover:rotate-90" />
              הוספה לסל
            </span>
            {/* Button Shimmer */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-500" />
          </Button>
          <span className="text-xl font-bold text-primary transition-all duration-300 group-hover:scale-110">
            {formatPrice(meal.price_in_cents)}
          </span>
        </div>
      </div>
      
      {/* Border Animation */}
      <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-primary/20 transition-colors duration-500 pointer-events-none" />
    </div>
  );
}
